import axios from 'axios';
import { authService } from './authService';
import { HTTP_API_BASE_URL } from '../config';

const api = axios.create({
  baseURL: HTTP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Cookie'lerin gönderilmesi için gerekli (cross-origin isteklerde)
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // İşletme sahibi: httpOnly cookie. Çalışan (staff/manager): JWT localStorage'da — header gerekli
    if (typeof localStorage !== 'undefined' && localStorage.getItem('userType') === 'employee') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Token refresh mekanizması
let isRefreshing = false;
let failedQueue = [];
let isHardRedirectingToAuth = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Eğer zaten auth sayfasına "hard redirect" yapıyorsak, yeni denemeleri durdur.
    if (isHardRedirectingToAuth) {
      return Promise.reject(error);
    }

    // Refresh endpoint'i 401 dönerse tekrar refresh denemeye çalışma (sonsuz döngüye girer).
    const requestUrl = String(originalRequest?.url || '');
    const isRefreshRequest =
      requestUrl.includes('/api/business/auth/refresh') ||
      requestUrl.endsWith('/api/business/auth/refresh');

    // 401 hatası ve token expired durumu
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      const isAuthLoginAttempt =
        requestUrl.includes('/api/business/auth/login') ||
        requestUrl.includes('/api/business/auth/staff/login');
      if (isAuthLoginAttempt) {
        return Promise.reject(error);
      }

      // Çalışan oturumu çerez yenileme kullanmaz; doğrudan çıkış
      if (typeof localStorage !== 'undefined' && localStorage.getItem('userType') === 'employee') {
        isHardRedirectingToAuth = true;
        authService.logout();
        window.location.href = '/auth/sign-in';
        return Promise.reject(error);
      }

      // Eğer zaten refresh işlemi yapılıyorsa, queue'ya ekle
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Token artık cookie'de, Authorization header'a gerek yok
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh: axios instance ile değil (döngü riski), aynı HTTP tabanı ile çağır
        const response = await axios.post(
          HTTP_API_BASE_URL ? `${HTTP_API_BASE_URL}/api/business/auth/refresh` : '/api/business/auth/refresh',
          {},
          {
            withCredentials: true // Cookie'leri göndermek için
          }
        );

        if (response.data?.success) {
          // Token artık httpOnly cookie'de, localStorage'a kaydetme
          // Cookie otomatik gönderilecek, Authorization header'a gerek yok
          
          // Queue'daki diğer istekleri de çöz
          processQueue(null, null); // Token artık cookie'de
          isRefreshing = false;
          
          return api(originalRequest);
        } else {
          throw new Error('Token yenileme başarısız');
        }
      } catch (refreshError) {
        // Refresh başarısız oldu
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // Safari / cross-site cookie sorunlarında refresh cookie hiç gelmeyebilir.
        // Bu durumda localStorage'daki "giriş yapılmış" state'i yanıltıcı olur; temizleyip girişe at.
        const refreshStatus = refreshError?.response?.status;
        const refreshMessage = refreshError?.response?.data?.message || refreshError?.message || '';
        const isMissingRefreshToken =
          refreshStatus === 401 &&
          /Refresh token bulunamadı|refresh token/i.test(String(refreshMessage));

        if (isMissingRefreshToken) {
          isHardRedirectingToAuth = true;
          authService.logout();
          window.location.href = '/auth/sign-in';
        }

        // Diğer hatalarda (ör. geçici ağ) mevcut state'i hemen silmeyelim.
        return Promise.reject(refreshError);
      }
    }

    // 403 durumunda (pending hariç) logout
    if (error.response?.status === 403 && error.response?.data?.status !== 'pending') {
      authService.logout();
      window.location.href = '/auth/sign-in';
    }

    return Promise.reject(error);
  }
);

export default api; 